'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Heart, ListMusic, ListPlus, Check, X } from 'lucide-react'
import { useFavorites, usePlaylists } from '@/lib/library'
import { usePlayer, type PlayerTrack } from '@/lib/player-context'
import { showToast } from '@/lib/toast'
import Artwork from '@/components/Artwork'

type Props = {
  track: PlayerTrack
  /** Taille du bouton trigger : 'sm' (TrackList) ou 'md' (PlayerScreen, vue lecteur) */
  size?: 'sm' | 'md'
}

/**
 * Bouton "+" qui ouvre un menu déroulant avec les destinations possibles :
 *  - ❤︎ Favoris (toggle)
 *  - ▶ File d'attente (enqueue immédiat)
 *  - Playlists existantes (chacune ajoute la piste)
 *  - + Nouvelle playlist (prompt inline pour le nom puis créé + ajoute)
 */
export default function AddToMenu({ track, size = 'sm' }: Props) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const { has: hasFav, toggle: toggleFav } = useFavorites()
  const { playlists, create, addTrack } = usePlaylists()
  const { enqueue } = usePlayer()
  const isFav = hasFav(track.videoId)

  // Position du menu sous le bouton
  const openMenu = () => {
    if (!btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    const menuWidth = 240
    const margin = 8
    // Préférence : juste à gauche du bouton (col droite des cartes), sinon à droite
    let x = r.right - menuWidth
    if (x < margin) x = margin
    if (x + menuWidth > window.innerWidth - margin) x = window.innerWidth - menuWidth - margin
    let y = r.bottom + 6
    // Si pas la place en bas, on l'ouvre au-dessus
    const estimatedHeight = Math.min(360, 60 + 38 * (3 + playlists.length))
    if (y + estimatedHeight > window.innerHeight - margin) {
      y = Math.max(margin, r.top - estimatedHeight - 6)
    }
    setPos({ x, y })
    setOpen(true)
  }

  // Fermer si clic dehors / Escape
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t)) return
      if (btnRef.current?.contains(t)) return
      setOpen(false); setCreating(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setCreating(false) }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => { setOpen(false); setCreating(false); setNewTitle('') }

  const onPickFav = () => {
    toggleFav(track)
    showToast({
      kind: 'success',
      msg: isFav ? 'Retiré des favoris' : 'Ajouté aux favoris',
      thumbnail: track.thumbnail,
      duration: 2200,
    })
    close()
  }

  const onPickQueue = () => {
    enqueue(track)
    showToast({ kind: 'success', msg: "Ajouté à la file d'attente", thumbnail: track.thumbnail, duration: 2200 })
    close()
  }

  const onPickPlaylist = (playlistId: string, title: string) => {
    addTrack(playlistId, track)
    showToast({ kind: 'success', msg: `Ajouté à « ${title} »`, thumbnail: track.thumbnail, duration: 2500 })
    close()
  }

  const onCreatePlaylist = (e?: React.FormEvent) => {
    e?.preventDefault()
    const title = newTitle.trim() || 'Nouvelle playlist'
    const id = create(title)
    addTrack(id, track)
    showToast({ kind: 'success', msg: `Playlist « ${title} » créée`, thumbnail: track.thumbnail, duration: 2800 })
    close()
  }

  const px = size === 'sm' ? 'p-2' : 'p-2.5'
  const ic = size === 'sm' ? 18 : 20

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => (open ? close() : openMenu())}
        title="Ajouter à…"
        className={`${px} rounded-xl transition shrink-0 border
          ${open ? 'text-a5 bg-a2/15 border-a2/50' : 'text-white/60 hover:text-white hover:bg-white/[0.07] border-transparent'}`}>
        <Plus size={ic} />
      </button>

      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', left: pos.x, top: pos.y, width: 240, zIndex: 1000, background: 'linear-gradient(180deg, #16183C 0%, rgb(var(--spanel)) 100%)' }}
          className="rounded-2xl border border-white/[0.08] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,.6),0_0_30px_rgb(var(--a2)/.18)]"
        >
          {/* Header miniature de la piste qu'on ajoute */}
          <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/[0.07] bg-white/[0.03]">
            {track.isVideo
              ? (track.thumbnail ? <img src={track.thumbnail} alt="" className="w-9 h-9 rounded-xl object-cover" /> : <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-adeep to-spanel" />)
              : <Artwork track={track} size={36} strict rounded="rounded-xl" className="shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-a4">Ajouter à…</div>
              <div className="text-xs font-semibold truncate">{track.title}</div>
            </div>
            <button onClick={close} aria-label="Fermer" className="w-7 h-7 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/[0.08]">
              <X size={14} />
            </button>
          </div>

          {/* Favoris */}
          <button
            onClick={onPickFav}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-a2/[0.12] transition"
          >
            <Heart size={16} className={isFav ? 'fill-a3 text-a3' : 'text-white/65'} />
            <span className="text-sm flex-1">{isFav ? 'Retirer des favoris' : 'Favoris'}</span>
            {isFav && <Check size={14} className="text-a4" />}
          </button>

          {/* File d'attente */}
          <button
            onClick={onPickQueue}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-a2/[0.12] transition border-t border-white/[0.06]"
          >
            <ListMusic size={16} className="text-white/65" />
            <span className="text-sm flex-1">File d'attente (lecture en cours)</span>
          </button>

          {/* Playlists existantes */}
          {playlists.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/40 border-t border-white/[0.06]">
                Mes playlists
              </div>
              <div className="max-h-48 overflow-y-auto">
                {playlists.map(p => {
                  const already = p.tracks.some(t => t.videoId === track.videoId)
                  return (
                    <button
                      key={p.id}
                      onClick={() => onPickPlaylist(p.id, p.title)}
                      disabled={already}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-a2/[0.12] transition disabled:opacity-50 disabled:cursor-default"
                    >
                      <ListMusic size={14} className="text-a4 shrink-0" />
                      <span className="text-sm flex-1 truncate">{p.title}</span>
                      {already
                        ? <Check size={13} className="text-emerald-400 shrink-0" />
                        : <span className="text-[10px] text-white/40">{p.tracks.length}</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Créer nouvelle playlist */}
          <div className="border-t border-white/[0.06]">
            {creating ? (
              <form onSubmit={onCreatePlaylist} className="p-2 flex items-center gap-2">
                <input
                  autoFocus
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="Nom de la playlist…"
                  className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded-xl px-2.5 py-1.5 text-xs focus:outline-none focus:border-a2/70"
                />
                <button type="submit" className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-white bg-gradient-to-r from-a1 to-a2 shadow-[0_0_14px_rgb(var(--a2)/.45)]">
                  Créer
                </button>
              </form>
            ) : (
              <button
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-a4 hover:bg-a2/[0.12] transition"
              >
                <ListPlus size={16} />
                <span className="text-sm flex-1 font-semibold">Nouvelle playlist…</span>
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
